/* ==========================================================================
   ARAMINO — interactions
   Reveal on scroll · custom accordion · carousel arrows
   ========================================================================== */

(function () {
  'use strict';

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Reveal on scroll ---------- */
  function initReveal() {
    const items = document.querySelectorAll('.ar-reveal');
    if (!items.length) return;

    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
    );

    items.forEach((el, i) => {
      // Respect hand-placed delays from markup; otherwise derive from index
      if (!el.style.getPropertyValue('--ar-delay')) {
        el.style.setProperty('--ar-delay', String(Math.min(i % 6, 5)));
      }
      observer.observe(el);
    });
  }

  /* ---------- Custom accordion (features section) ---------- */
  function initAccordions() {
    const accordions = document.querySelectorAll('.ar-accordion details');

    accordions.forEach((details) => {
      const summary = details.querySelector('summary');
      const body = details.querySelector('.ar-acc-body');

      if (!summary || !body) return;

      if (!prefersReducedMotion) {
        body.style.transition = 'max-height 0.4s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.4s ease';
        body.style.overflow = 'hidden';
        body.style.opacity = '0';
        body.style.maxHeight = '0px';
        if (details.open) {
          body.style.maxHeight = body.scrollHeight + 'px';
          body.style.opacity = '1';
        }
      }

      summary.addEventListener('click', (e) => {
        if (prefersReducedMotion) return;

        // Close siblings for a clean single-open accordion
        accordions.forEach((other) => {
          if (other !== details && other.open) {
            other.open = false;
            const otherBody = other.querySelector('.ar-acc-body');
            if (otherBody) {
              otherBody.style.maxHeight = '0px';
              otherBody.style.opacity = '0';
            }
          }
        });

        // Let the browser toggle `open` first, then animate
        requestAnimationFrame(() => {
          if (details.open) {
            body.style.maxHeight = body.scrollHeight + 'px';
            body.style.opacity = '1';
          } else {
            body.style.maxHeight = '0px';
            body.style.opacity = '0';
          }
        });
      });
    });

    // Handle window resize so max-height stays accurate
    window.addEventListener('resize', () => {
      accordions.forEach((details) => {
        const body = details.querySelector('.ar-acc-body');
        if (details.open && body) body.style.maxHeight = body.scrollHeight + 'px';
      });
    });
  }

  /* ---------- Category carousel arrows ---------- */
  function initCarousels() {
    document.querySelectorAll('.ar-carousel').forEach((carousel) => {
      const track = carousel.querySelector('.ar-carousel__track');
      const prev = carousel.querySelector('.ar-carousel__arrow--prev');
      const next = carousel.querySelector('.ar-carousel__arrow--next');
      if (!track || !prev || !next) return;

      const scrollBy = () => {
        const item = track.querySelector('.ar-carousel__item');
        return item ? item.getBoundingClientRect().width + 24 : 280;
      };

      prev.addEventListener('click', () => {
        track.scrollBy({ left: -scrollBy(), behavior: 'smooth' });
      });
      next.addEventListener('click', () => {
        track.scrollBy({ left: scrollBy(), behavior: 'smooth' });
      });

      // Hide/show arrows at the ends
      const updateArrows = () => {
        const max = track.scrollWidth - track.clientWidth - 4;
        prev.style.opacity = track.scrollLeft <= 4 ? '0.35' : '1';
        next.style.opacity = track.scrollLeft >= max ? '0.35' : '1';
      };
      track.addEventListener('scroll', updateArrows, { passive: true });
      updateArrows();
    });
  }

  /* ---------- Header: transparent at top, solid white on scroll ---------- */
  function initHeaderBlend() {
    const hero = document.querySelector('.ar-hero');
    if (!hero) return;

    const bodyEl = document.body;
    bodyEl.classList.add('ar-has-hero');

    if (prefersReducedMotion) {
      bodyEl.style.setProperty('--ar-header-solid', '1');
      return;
    }

    // Pull the hero up so it starts at the very top, under the header.
    const headerSections = [
      document.querySelector('.section-header'),
      document.querySelector('.announcement-bar-section'),
    ].filter(Boolean);

    const applyHeaderHeight = () => {
      if (!headerSections.length) return;
      const height = headerSections.reduce((sum, el) => sum + el.offsetHeight, 0);
      bodyEl.style.setProperty('--ar-header-h', height + 'px');
    };

    // Progressively fill the header with white as the user scrolls.
    const update = () => {
      const vh = window.innerHeight || 1;
      const ratio = Math.min(1, Math.max(0, window.scrollY / (vh * 0.5)));
      bodyEl.style.setProperty('--ar-header-solid', ratio.toFixed(3));
    };

    applyHeaderHeight();
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', () => {
      applyHeaderHeight();
      update();
    });
    window.addEventListener('load', applyHeaderHeight);
  }

  /* ---------- Product price pill (variant sync) ---------- */
  function initPricePill() {
    if (typeof subscribe !== 'function' || !window.PUB_SUB_EVENTS) return;

    subscribe(window.PUB_SUB_EVENTS.variantChange, ({ data }) => {
      if (!data || !data.sectionId) return;
      const info = document.querySelector(`product-info[data-section="${data.sectionId}"]`);
      if (!info) return;

      const pill = info.querySelector('.ar-price-pill');
      const source = info.querySelector(`#price-${data.sectionId} .price`);
      if (!pill || !source) return;

      const priceEl = pill.querySelector('.ar-price-pill__price');
      const compareEl = pill.querySelector('.ar-price-pill__compare');
      const sale = source.querySelector('.price-item--sale');
      const regular = source.querySelector('.price-item--regular');
      const main = sale || regular || source.querySelector('.price-item');

      if (priceEl && main) {
        priceEl.textContent = main.textContent.trim();
      }

      if (compareEl) {
        if (source.classList.contains('price--on-sale') && regular) {
          compareEl.textContent = regular.textContent.trim();
          compareEl.style.display = '';
        } else {
          compareEl.style.display = 'none';
        }
      }
    });
  }

  /* ---------- Init ---------- */
  function init() {
    initReveal();
    initAccordions();
    initCarousels();
    initHeaderBlend();
    initPricePill();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
